import React, { useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { FuelGauge } from '../vehicle/FuelGauge';
import { Vehicle, Driver, VehicleDamage } from '../../types';
import theme from '../../theme';

interface CarReceivingFormData {
  vehicleId: string;
  driverId: string;
  secondaryDriverId?: string;
  receivedBy: string;
  receivedAt: string;
  mileageIn: number;
  fuelLevel: number;
  damages: VehicleDamage[];
  photos: string[];
  notes: string;
  keyStatus: 'received' | 'missing' | 'damaged';
  documentsReceived: string[];
  interiorCondition: 'excellent' | 'good' | 'fair' | 'poor';
  exteriorCondition: 'excellent' | 'good' | 'fair' | 'poor';
}

interface CarReceivingFormProps {
  vehicle?: Vehicle;
  driver?: Driver;
  secondaryDriver?: Driver;
  onSubmit: (data: CarReceivingFormData) => void;
  onCancel: () => void;
}

export const CarReceivingForm: React.FC<CarReceivingFormProps> = ({
  vehicle,
  driver,
  secondaryDriver,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<CarReceivingFormData>({
    vehicleId: vehicle?.id || '',
    driverId: driver?.id || '',
    secondaryDriverId: secondaryDriver?.id,
    receivedBy: 'Fleet Manager', // Default value
    receivedAt: new Date().toISOString().slice(0, 16),
    mileageIn: 0,
    fuelLevel: 50,
    damages: [],
    photos: [],
    notes: '',
    keyStatus: 'received',
    documentsReceived: [],
    interiorCondition: 'good',
    exteriorCondition: 'good'
  });

  const updateFormData = (field: keyof CarReceivingFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    console.log('� BUTTON CLICKED - handleSubmit called!');
    alert('Button was clicked! Check console.');
    
    console.log('�🚗 CarReceivingForm: handleSubmit called');
    console.log('🚗 Form data:', formData);
    
    if (!formData.vehicleId || !formData.driverId || !formData.receivedBy) {
      console.log('❌ Missing required fields:', {
        vehicleId: formData.vehicleId,
        driverId: formData.driverId,
        receivedBy: formData.receivedBy
      });
      
      let missingFields = [];
      if (!formData.vehicleId) missingFields.push('Vehicle');
      if (!formData.driverId) missingFields.push('Driver');
      if (!formData.receivedBy) missingFields.push('Received By');
      
      Alert.alert('Error', `Please fill in: ${missingFields.join(', ')}`);
      return;
    }
    
    console.log('✅ All fields valid, calling onSubmit');
    onSubmit(formData);
  };

  return (
    <View style={styles.container}>
      {/* A4 Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Ionicons name="car" size={32} color={theme.colors.primary} />
          <View style={styles.headerText}>
            <Text style={styles.headerTitle}>VEHICLE RECEIVING FORM</Text>
            <Text style={styles.headerSubtitle}>STSC Fleet Management</Text>
          </View>
        </View>
        <Text style={styles.formNumber}>Form #: VRF-{new Date().getTime()}</Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Assignment Information */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Assignment Information</Text>
          
          {vehicle && (
            <View style={styles.vehicleInfo}>
              <Text style={styles.infoTitle}>Vehicle Details</Text>
              <Text style={styles.infoText}>Plate: {vehicle.licensePlate}</Text>
              <Text style={styles.infoText}>Make/Model: {vehicle.makeModel}</Text>
              <Text style={styles.infoText}>Year: {vehicle.year}</Text>
              <Text style={styles.infoText}>Fleet Type: {vehicle.fleetType}</Text>
            </View>
          )}

          {driver && (
            <View style={styles.driverInfo}>
              <Text style={styles.infoTitle}>Primary Driver Details</Text>
              <Text style={styles.infoText}>Name: {driver.name}</Text>
              <Text style={styles.infoText}>ID: {driver.licenseNumber}</Text>
              <Text style={styles.infoText}>Phone: {driver.phoneNumber}</Text>
            </View>
          )}

          {secondaryDriver && (
            <View style={[styles.driverInfo, styles.secondaryDriverInfo]}>
              <Text style={[styles.infoTitle, styles.secondaryDriverTitle]}>Secondary Driver Details</Text>
              <Text style={styles.infoText}>Name: {secondaryDriver.name}</Text>
              <Text style={styles.infoText}>ID: {secondaryDriver.licenseNumber}</Text>
              <Text style={styles.infoText}>Phone: {secondaryDriver.phoneNumber}</Text>
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Received By *</Text>
            <TextInput
              style={styles.textInput}
              value={formData.receivedBy}
              onChangeText={(value) => updateFormData('receivedBy', value)}
              placeholder="Enter receiver name"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Received Date & Time *</Text>
            <TextInput
              style={styles.textInput}
              value={formData.receivedAt}
              onChangeText={(value) => updateFormData('receivedAt', value)}
              placeholder="YYYY-MM-DD HH:MM"
            />
          </View>
        </Card>

        {/* Vehicle Condition */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Vehicle Condition</Text>
          
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Current Mileage</Text>
            <TextInput
              style={styles.textInput}
              value={formData.mileageIn.toString()}
              onChangeText={(value) => updateFormData('mileageIn', parseInt(value) || 0)}
              keyboardType="numeric"
              placeholder="Enter mileage"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Fuel Level</Text>
            <FuelGauge 
              value={formData.fuelLevel} 
              onChange={(value) => updateFormData('fuelLevel', value)}
              editable={true}
            />
          </View>

          <View style={styles.conditionRow}>
            <View style={styles.conditionItem}>
              <Text style={styles.inputLabel}>Exterior Condition</Text>
              <View style={styles.radioGroup}>
                {['excellent', 'good', 'fair', 'poor'].map((condition) => (
                  <TouchableOpacity
                    key={condition}
                    style={styles.radioOption}
                    onPress={() => updateFormData('exteriorCondition', condition)}
                  >
                    <Ionicons
                      name={formData.exteriorCondition === condition ? 'radio-button-on' : 'radio-button-off'}
                      size={20}
                      color={theme.colors.primary}
                    />
                    <Text style={styles.radioText}>{condition.charAt(0).toUpperCase() + condition.slice(1)}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.conditionItem}>
              <Text style={styles.inputLabel}>Interior Condition</Text>
              <View style={styles.radioGroup}>
                {['excellent', 'good', 'fair', 'poor'].map((condition) => (
                  <TouchableOpacity
                    key={condition}
                    style={styles.radioOption}
                    onPress={() => updateFormData('interiorCondition', condition)}
                  >
                    <Ionicons
                      name={formData.interiorCondition === condition ? 'radio-button-on' : 'radio-button-off'}
                      size={20}
                      color={theme.colors.primary}
                    />
                    <Text style={styles.radioText}>{condition.charAt(0).toUpperCase() + condition.slice(1)}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Key Status</Text>
            <View style={styles.radioGroup}>
              {[
                { value: 'received', label: 'Received' },
                { value: 'missing', label: 'Missing' },
                { value: 'damaged', label: 'Damaged' }
              ].map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={styles.radioOption}
                  onPress={() => updateFormData('keyStatus', option.value)}
                >
                  <Ionicons
                    name={formData.keyStatus === option.value ? 'radio-button-on' : 'radio-button-off'}
                    size={20}
                    color={theme.colors.primary}
                  />
                  <Text style={styles.radioText}>{option.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Additional Notes</Text>
            <TextInput
              style={[styles.textInput, styles.textArea]}
              value={formData.notes}
              onChangeText={(value) => updateFormData('notes', value)}
              placeholder="Enter any additional notes or observations"
              multiline
              numberOfLines={4}
            />
          </View>
        </Card>

        {/* Signatures */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Signatures</Text>
          
          <View style={styles.signatureRow}>
            <View style={styles.signatureBox}>
              <Text style={styles.signatureLabel}>Driver Signature</Text>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureDate}>Date: ________________</Text>
            </View>
            
            <View style={styles.signatureBox}>
              <Text style={styles.signatureLabel}>Receiver Signature</Text>
              <View style={styles.signatureLine} />
              <Text style={styles.signatureDate}>Date: ________________</Text>
            </View>
          </View>
        </Card>
      </ScrollView>

      {/* Action Buttons */}
      <View style={styles.footer}>
        <Button
          title="Cancel"
          variant="outline"
          onPress={onCancel}
          style={styles.footerButton}
        />
        <TouchableOpacity
          style={[styles.footerButton, { backgroundColor: 'blue', padding: 15, borderRadius: 8 }]}
          onPress={handleSubmit}
        >
          <Text style={{ color: 'white', textAlign: 'center', fontWeight: 'bold' }}>
            Complete Assignment (Test)
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.white,
  },
  header: {
    backgroundColor: theme.colors.white,
    padding: theme.spacing.lg,
    borderBottomWidth: 2,
    borderBottomColor: theme.colors.primary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerText: {
    marginLeft: theme.spacing.md,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  formNumber: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    fontFamily: 'monospace',
  },
  content: {
    flex: 1,
    padding: theme.spacing.lg,
  },
  section: {
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
    paddingBottom: theme.spacing.sm,
  },
  vehicleInfo: {
    marginBottom: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
  },
  driverInfo: {
    marginBottom: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.blue50,
    borderRadius: theme.borderRadius.md,
  },
  secondaryDriverInfo: {
    backgroundColor: theme.colors.green50,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.green500,
  },
  secondaryDriverTitle: {
    color: theme.colors.green700,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  infoText: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    marginBottom: 2,
  },
  inputGroup: {
    marginBottom: theme.spacing.md,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  textInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.sm,
    fontSize: 14,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.white,
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  conditionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.md,
  },
  conditionItem: {
    flex: 0.48,
  },
  radioGroup: {
    flexDirection: 'column',
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  radioText: {
    marginLeft: theme.spacing.xs,
    fontSize: 13,
    color: theme.colors.textPrimary,
  },
  signatureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  signatureBox: {
    flex: 0.48,
    alignItems: 'center',
  },
  signatureLabel: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  signatureLine: {
    width: '100%',
    height: 60,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray400,
    marginBottom: theme.spacing.sm,
  },
  signatureDate: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  footer: {
    flexDirection: 'row',
    padding: theme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
    backgroundColor: theme.colors.white,
  },
  footerButton: {
    flex: 1,
    marginHorizontal: theme.spacing.xs,
  },
});

export default CarReceivingForm;
